'use client';

import React, { useEffect, useRef } from 'react';
import { MessageCircle, Mail, MapPin, Clock, BadgeCheck } from 'lucide-react';

export default function CTASection() {
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const bannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let ctx: { revert: () => void };
    const init = async () => {
      const { gsap } = await import('gsap');
      const { ScrollTrigger } = await import('gsap/ScrollTrigger');
      gsap.registerPlugin(ScrollTrigger);
      ctx = gsap.context(() => {
        gsap.fromTo(contentRef.current, { opacity: 0, y: 32, filter: 'blur(8px)' },
          { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.8, ease: 'power2.out', scrollTrigger: { trigger: sectionRef.current, start: 'top 75%' } });
        gsap.fromTo(bannerRef.current, { opacity: 0, y: 48, scale: 0.98 },
          { opacity: 1, y: 0, scale: 1, duration: 1.0, ease: 'power2.out', scrollTrigger: { trigger: bannerRef.current, start: 'top 80%' } });
      }, sectionRef);
    };
    init();
    return () => { if (ctx) ctx.revert(); };
  }, []);

  return (
    <section id="kontak" ref={sectionRef} className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div ref={contentRef} className="opacity-0 mb-12 md:mb-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-lime-500" />
            <span className="text-lime-500 text-[11px] font-semibold tracking-[0.2em] uppercase">Mulai Sekarang</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-light text-white tracking-tight leading-[1.1]">
            Siap Berkreasi{' '}
            <span className="font-semibold" style={{ background: 'linear-gradient(135deg, #a3e635, #84cc16)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Bersama Kami</span>?
          </h2>
        </div>

        <div ref={bannerRef} className="opacity-0">
          <div className="spotlight-card rounded-2xl p-[1px]" style={{ background: 'radial-gradient(circle, rgb(24,24,27) 0%, rgb(0,0,0) 100%)' }}>
            <div className="rounded-[15px] bg-[rgba(15,15,18,0.9)] relative overflow-hidden p-10 md:p-16 text-center">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full pointer-events-none"
                style={{ background: 'radial-gradient(ellipse, rgba(163,230,53,0.08) 0%, transparent 70%)' }} />

              <div className="relative z-10 flex flex-col items-center gap-6 max-w-2xl mx-auto">
                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-lime-400/30 bg-lime-400/10 text-lime-400 text-[11px] font-semibold tracking-[0.15em] uppercase">
                  <span className="w-1.5 h-1.5 rounded-full bg-lime-400 animate-pulse" />
                  Mulai Hari Ini
                </span>

                <p className="text-zinc-400 text-base md:text-lg leading-relaxed max-w-lg">
                  Ceritakan ide kreatif atau kebutuhan bisnis Anda. Konsultasi pertama gratis — tanpa komitmen, tanpa tekanan.
                </p>

                <div className="flex flex-col sm:flex-row items-center gap-4 mt-2">
                  <a href="https://wa.me/6281234567890" target="_blank" rel="noopener noreferrer"
                    className="px-10 py-4 text-sm font-semibold bg-white text-zinc-900 hover:bg-[#a3e635] hover:text-white transition-all duration-150 inline-flex items-center gap-2 w-full sm:w-auto justify-center">
                    <MessageCircle size={18} /> Hubungi Kami
                  </a>
                  <a href="mailto:hello@studo.id"
                    className="text-sm font-medium text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-2">
                    <Mail size={14} /> hello@studo.id
                  </a>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8 mt-4 pt-6 border-t border-white/[0.06] w-full justify-center">
                  <div className="flex items-center gap-2 text-zinc-500 text-[13px]">
                    <MapPin size={14} className="text-lime-500" /> Bandung, Indonesia
                  </div>
                  <div className="flex items-center gap-2 text-zinc-500 text-[13px]">
                    <Clock size={14} className="text-lime-500" /> Senin–Jumat, 09:00–18:00 WIB
                  </div>
                  <div className="flex items-center gap-2 text-zinc-500 text-[13px]">
                    <BadgeCheck size={14} className="text-lime-500" /> Respons dalam 24 jam
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
